import * as vp from 'vopay';

import { ChangeEvent, useImperativeHandle, useMemo, useEffect, Ref, FC } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from '@/components/Form/utils';
import { FieldData } from '@/components/Form/types';
import { type PermissionsMap, BodyText, MaterialSymbol, Tooltip, withStyles } from 'vopay';
import { Title, UtilButton } from 'UI';
import CollapsibleList from './CollapsibleList';
import styles from './AccountUserPermissionsForm.module.scss';

const { Box, SwitchIndicator } = vp;
const classes = withStyles(styles);

export type PermissionFormValues = {
  [permissionKey: string]: boolean;
};

type AccountUserPermissionsFormProps = {
  permissions: PermissionsMap;
  onSubmit: (values: PermissionFormValues) => void;
  ref: Ref<AccountUserPermissionsFormRef>;
  onFormStateChange: (hasChanges: boolean, isValid: boolean) => void;
  onAllEnabledChange?: (allEnabled: boolean) => void;
  /**
   * Form id used for the footer submit button (`form="..."`). Required so
   * the same component can be reused across drawers that each need a
   * distinct id.
   */
  id: string;
  /**
   * Search term used to filter the permissions list. Parent owns the value
   * and renders its own <PermissionsSearchField/> (typically in the drawer
   * header).
   */
  searchTerm: string;
  /**
   * Permission keys inherited from one or more assigned roles. These render
   * as checked + disabled (the role grants them, not the user-individual
   * layer).
   *
   * SIDE EFFECT TO BE AWARE OF: keys in this Set are **stripped from the
   * `onSubmit` payload** before it is invoked. This is
   * correct for the Edit User Permissions drawer (where the role
   * assignments are managed separately and we don't want orphan individual
   * grants surviving role un-assignment). It is NOT necessarily correct
   * for other consumers of this form — if a future flow uses
   * `lockedPermissions` to render-only-lock permissions while still
   * expecting them in the payload, this stripping will silently break it.
   * Default is an empty Set → no stripping, which matches the historical
   * behaviour for CreateRoleDrawer / EditRoleDrawer.
   */
  lockedPermissions?: Set<string>;
};

export type AccountUserPermissionsFormRef = {
  toggleAll: (enabled: boolean) => void;
};

type ToggleProps = {
  name: string;
  checked: boolean;
  disabled?: boolean;
  /** Shown on hover when the toggle is disabled. */
  disabledTooltip?: string;
  onChange: (e: ChangeEvent<HTMLInputElement>) => void;
};
const Toggle: FC<ToggleProps> = ({ name, checked, disabled = false, disabledTooltip, onChange }) => {
  const toggleNode = (
    <span className={classes('toggleWrapper', disabled && 'toggleDisabled')}>
      <SwitchIndicator checked={checked} />
      <input
        className={classes('hiddenCheckbox')}
        id={`checkbox-${name}`}
        type="checkbox"
        name={name}
        checked={checked}
        disabled={disabled}
        onChange={onChange}
      />
    </span>
  );

  if (disabled && disabledTooltip) {
    return (
      <Tooltip
        type="hover"
        position="left"
        hasButton={false}
        customElement={toggleNode}
        information={disabledTooltip}
      />
    );
  }

  return toggleNode;
};

function buildPermissionsConfig(
  permissions: PermissionsMap,
  lockedPermissions: Set<string>,
  lockedTooltip: string,
): Record<string, FieldData<PermissionFormValues>[]> {
  const config: Record<string, FieldData<PermissionFormValues>[]> = {};

  Object.entries(permissions).forEach(([groupName, permissionGroup]) => {
    const groupFields: FieldData<PermissionFormValues>[] = [];

    Object.entries(permissionGroup).forEach(([permissionKey, permissionItem]) => {
      const isLocked = lockedPermissions.has(permissionKey);
      groupFields.push({
        fieldName: permissionKey,
        label: () => permissionItem.label,
        getInputState: () => 'optional',
        showField: () => true,
        renderField: ({ getFormikData }) => {
          const fieldData = getFormikData<boolean>({});
          return (
            <Toggle
              name={fieldData.name}
              checked={fieldData.value}
              disabled={isLocked}
              disabledTooltip={isLocked ? lockedTooltip : undefined}
              onChange={fieldData.onChange}
            />
          );
        },
      });
    });

    config[groupName] = groupFields;
  });

  return config;
}

function buildInitialValues(
  permissions: PermissionsMap,
  lockedPermissions: Set<string>,
): PermissionFormValues {
  const initialValues: PermissionFormValues = {};

  Object.values(permissions).forEach((permissionGroup) => {
    Object.entries(permissionGroup).forEach(([permissionKey, permissionItem]) => {
      // Role-provided permissions are always enabled in the UI regardless of
      // what the user's individual-grant layer says; the role grants them.
      initialValues[permissionKey] = lockedPermissions.has(permissionKey) || permissionItem.permitted;
    });
  });

  return initialValues;
}

const EMPTY_LOCKED: Set<string> = new Set();

const AccountUserPermissionsForm: FC<AccountUserPermissionsFormProps> = ({
  onSubmit,
  permissions,
  onFormStateChange,
  onAllEnabledChange,
  ref,
  id,
  searchTerm,
  lockedPermissions = EMPTY_LOCKED,
}) => {
  const { t } = useTranslation('translations', { keyPrefix: 'Users.UserPermissions.form' });

  const lockedTooltip = t('lockedTooltip', {
    defaultValue: 'This permission is non-editable since it is determined by the assigned role(s).',
  });

  const initialValues = useMemo(
    () => buildInitialValues(permissions, lockedPermissions),
    [permissions, lockedPermissions],
  );
  const permissionsConfig = useMemo(
    () => buildPermissionsConfig(permissions, lockedPermissions, lockedTooltip),
    [permissions, lockedPermissions, lockedTooltip],
  );

  const { ctx, renderFormElements } = useForm<PermissionFormValues>({
    config: Object.values(permissionsConfig).flat(),
    initialValues,
    onSubmit: (values) => {
      // Strip role-locked permissions from the save payload. Their state is
      // controlled by the role assignment, not the user-individual layer —
      // sending them would create orphan individual grants that survive role
      // un-assignment.
      const payload: PermissionFormValues = {};
      Object.entries(values).forEach(([key, value]) => {
        if (!lockedPermissions.has(key)) payload[key] = value;
      });
      onSubmit(payload);
    },
  });

  // Check if form has changes and is valid
  const hasChanges = useMemo(() => {
    return JSON.stringify(ctx.values) !== JSON.stringify(initialValues);
  }, [ctx.values, initialValues]);

  const hasAtLeastOnePermission = useMemo(() => {
    return Object.values(ctx.values).some((value) => value === true);
  }, [ctx.values]);

  // Notify parent of form state changes
  useEffect(() => {
    if (onFormStateChange) {
      onFormStateChange(hasChanges, hasAtLeastOnePermission);
    }
  }, [hasChanges, hasAtLeastOnePermission, onFormStateChange]);

  // Track how many permissions are enabled per group
  const groupStats = useMemo(() => {
    const stats: Record<string, { enabled: number; total: number }> = {};

    Object.entries(permissionsConfig).forEach(([groupName, groupFields]) => {
      const total = groupFields.length;
      const enabled = groupFields.filter((field) => ctx.values[field.fieldName]).length;
      stats[groupName] = { enabled, total };
    });

    return stats;
  }, [permissionsConfig, ctx.values]);

  // Function to enable all permissions in a specific group
  // (role-locked permissions are skipped — they're already on and immutable)
  const enableAllInGroup = (groupName: string) => {
    const groupFields = permissionsConfig[groupName];
    if (!groupFields) return;

    const updates: Record<string, boolean> = {};
    groupFields.forEach((field) => {
      if (!lockedPermissions.has(field.fieldName)) updates[field.fieldName] = true;
    });
    ctx.setValues({ ...ctx.values, ...updates });
  };

  // Function to disable all permissions in a specific group
  // (role-locked permissions are skipped — they can't be disabled here)
  const disableAllInGroup = (groupName: string) => {
    const groupFields = permissionsConfig[groupName];
    if (!groupFields) return;

    const updates: Record<string, boolean> = {};
    groupFields.forEach((field) => {
      if (!lockedPermissions.has(field.fieldName)) updates[field.fieldName] = false;
    });
    ctx.setValues({ ...ctx.values, ...updates });
  };

  // Function to toggle all permissions (skipping role-locked ones)
  const toggleAll = (enabled: boolean) => {
    const updates: Record<string, boolean> = {};
    Object.values(permissionsConfig)
      .flat()
      .forEach((field) => {
        if (!lockedPermissions.has(field.fieldName)) updates[field.fieldName] = enabled;
      });
    ctx.setValues({ ...ctx.values, ...updates });
  };

  // Notify parent when allEnabled state changes
  useEffect(() => {
    if (onAllEnabledChange) {
      const allFields = Object.values(permissionsConfig).flat();
      const allEnabled = allFields.length > 0 && allFields.every((field) => ctx.values[field.fieldName] === true);
      onAllEnabledChange(allEnabled);
    }
  }, [permissionsConfig, ctx.values, onAllEnabledChange]);

  // Expose methods to parent via ref
  useImperativeHandle(ref, () => ({
    toggleAll,
  }));

  // Filter permissions based on search term
  const filteredPermissionsConfig = useMemo(() => {
    if (!searchTerm.trim()) {
      return permissionsConfig;
    }

    const lowerSearchTerm = searchTerm.toLowerCase();
    const filtered: Record<string, FieldData<PermissionFormValues>[]> = {};

    Object.entries(permissionsConfig).forEach(([groupName, groupFields]) => {
      const groupMatches = groupName.toLowerCase().includes(lowerSearchTerm);

      // Filter permissions within the group by checking their labels
      const matchingPermissions = groupFields.filter((field) => {
        const label = field.label?.(ctx) || '';
        return String(label).toLowerCase().includes(lowerSearchTerm);
      });

      if (groupMatches || matchingPermissions.length > 0) {
        filtered[groupName] = groupMatches ? groupFields : matchingPermissions;
      }
    });

    return filtered;
  }, [permissionsConfig, searchTerm]);

  const hasFilteredResults = Object.keys(filteredPermissionsConfig).length > 0;

  return (
    <form onSubmit={ctx.handleSubmit} id={id} className={classes('formContainer')}>
      {!hasFilteredResults && searchTerm.trim() ? (
        <div className={classes('emptyStateWrapper')}>
          <Box flx="col" ai="center" jc="center" gap="sm">
            <MaterialSymbol icon="manage_search" size="90px" color="rgba(45, 45, 45, 0.2)" />
            <Title level="4">{t('permissionNotFound')}</Title>
          </Box>
        </div>
      ) : (
        <CollapsibleList>
          {Object.entries(filteredPermissionsConfig).map(([groupName, groupFields]) => {
            const stats = groupStats[groupName];
            const allEnabled = stats.enabled === stats.total;
            const isFiltering = searchTerm.trim().length > 0;

            // If every permission in this group is role-locked, the user
            // can't toggle anything — hide the Enable All / Disable All link
            // (matches the Figma where role-saturated groups have no link).
            const hasAnyUnlocked = groupFields.some(
              (field) => !lockedPermissions.has(field.fieldName),
            );

            return (
              <CollapsibleList.Item key={groupName} defaultOpen={false} open={isFiltering}>
                <CollapsibleList.Trigger>
                  <Box flx="row" jc="sb" ai="center" grow="1">
                    <Box ai="center" gapSize="12px">
                      <Title level="5">{groupName}</Title>
                      <BodyText weight="rg" className={classes('groupStatsText')}>
                        ({stats.enabled}/{stats.total})
                      </BodyText>
                    </Box>
                    {!isFiltering && hasAnyUnlocked && (
                      <div onClick={(e) => e.stopPropagation()}>
                        <Box gap="sm">
                          {allEnabled ? (
                            <UtilButton size="medium" onClick={() => disableAllInGroup(groupName)}>
                              {t('disableAll')}
                            </UtilButton>
                          ) : (
                            <UtilButton size="medium" onClick={() => enableAllInGroup(groupName)}>
                              {t('enableAll')}
                            </UtilButton>
                          )}
                        </Box>
                      </div>
                    )}
                  </Box>
                </CollapsibleList.Trigger>

                <CollapsibleList.Content>
                  <Box flx="col" gap="md" base="8">
                    {renderFormElements({
                      config: groupFields,
                      override: (fieldData) => {
                        return (
                          <label className={classes('permissionLabel')} htmlFor={`checkbox-${fieldData.fieldName}`}>
                            <BodyText className={classes('permissionLabelText')} weight="md">
                              {fieldData.label}
                            </BodyText>

                            {fieldData.template}
                          </label>
                        );
                      },
                    })}
                  </Box>
                </CollapsibleList.Content>
              </CollapsibleList.Item>
            );
          })}
        </CollapsibleList>
      )}
    </form>
  );
};

export default AccountUserPermissionsForm;
